import React, { useEffect, useRef, useState } from "react";

import {
  AppBar,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  styled,
  Avatar,
  Tabs,
  Tab,
  Badge,
  Button,
  ButtonBase,
  TabScrollButton,
} from "@mui/material";
import {
  Call,
  ChevronLeft,
  ChevronRight,
  Clear,
  VideoCall,
} from "@mui/icons-material";
import { useLoaderData, useParams, useSearchParams } from "react-router-dom";
import invoice_logo from "./invoice_logo.png";
import MaleAvatar from "./male-avtar.png";
import FemaleAvatar from "./female-avtar.png";
import NeutralAvatar from "./account.png";
import { API_URL } from "../helpers/variables";

const CustomToolbar = styled(Toolbar)(({ theme }) => ({
  boxShadow: "0 .125rem .25rem rgba(0,0,0,.075)",
  borderRadius: "8px",
  padding: 0,
  alignItems: "center",
  gap: "8px",
}));

const ChatHeader = () => {
  const [searchParams] = useSearchParams();

  const {
    corporationName,
    vendorName,
    invoiceAmount,
    paymentMethod,
    authID,
    chatStatus,
    type = "bill",
    roomName,
  } = Object.fromEntries(searchParams.entries());

  const { room, user } = useLoaderData();
  let { userID } = user;

  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    const eventSource = new EventSource(
      `${API_URL}/participates_status?room_id=${room["_id"]}`
    );

    eventSource.onmessage = (event) => {
      let data = JSON.parse(event.data);
      data = data.filter((i) => i["userID"] !== userID);
      data = data.filter((i) => !i["removed"])

      const onlineUsers = data
        .filter((i) => i.isOnline)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      const offlineUsers = data
        .filter((i) => !i.isOnline)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      setParticipants([...onlineUsers, ...offlineUsers]);
    };

    return () => {
      eventSource.close();
    };
  }, []);

  const tabsRef = useRef(null);
  const [hiddenCount, setHiddenCount] = useState({ left: 0, right: 0 });

  useEffect(() => {
    const tabList = tabsRef.current?.querySelector('[role="tablist"]');
    const tabs = tabList?.children;

    if (!tabs) return;

    const visibleStart = tabList.parentElement.scrollLeft;
    const visibleEnd = visibleStart + tabList.clientWidth;

    let left = 0;
    let right = 0;

    Array.from(tabs).forEach((tab) => {
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;

      if (tabRight < visibleStart) left++;
      else if (tabLeft > visibleEnd) right++;
    });

    setHiddenCount({ left, right });
  }, [participants]);

  const [page, setPage] = useState(0);
  const itemsPerPage = 4;

  const totalPages = Math.ceil(participants.length / itemsPerPage);

  const handleNext = () => {
    setPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrev = () => {
    setPage((prev) => Math.max(prev - 1, 0));
  };

  const startIndex = page * itemsPerPage;
  const currentItems = participants.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <Stack p={1} gap={0.5}>
      <CustomToolbar sx={{ bgcolor: "#a9e3e3", px: 2, py: 1 }}>
        {page !== 0 && (
          <IconButton onClick={handlePrev}>
            <ChevronLeft />
          </IconButton>
        )}

        {currentItems.map((i, index) => (
          <Stack
            key={startIndex + index}
            alignItems={"center"}
            gap={0.5}
            width={"100%"}
          >
            <Badge
              color="success"
              variant="dot"
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              invisible={!i.isOnline}
            >
              <Avatar
                sx={{ width: 35, height: 35 }}
                src={
                  i.gender === "male"
                    ? MaleAvatar
                    : i.gender === "female"
                    ? FemaleAvatar
                    : NeutralAvatar
                }
              />
            </Badge>
            <Typography whiteSpace={"nowrap"} variant="caption">
              {i.displayName}
            </Typography>
          </Stack>
        ))}

        {page < totalPages - 1 && (
          <Button
            disableRipple
            onClick={handleNext}
            sx={{ ml: "auto", whiteSpace: "nowrap", textTransform: "none" }}
          >
            +{participants.length - (startIndex + currentItems.length)} More
          </Button>
        )}
      </CustomToolbar>

      <CustomToolbar>
        {type === "bill" && <img width={64} src={invoice_logo} />}

        <Stack width={"100%"}>
          <Typography variant="caption" color="text.secondary">
            {corporationName}
          </Typography>

          <Typography fontSize={12} fontWeight={"bold"}>
            {vendorName}
          </Typography>

          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            width={"100%"}
            alignItems={"center"}
          >
            <Typography fontSize={12} color="#4846ff" fontWeight={"bold"}>
              {invoiceAmount}
            </Typography>
            <Typography fontWeight={"bold"}>{roomName}</Typography>
            <Box />
          </Stack>

          <Typography fontSize={12}>{paymentMethod}</Typography>

          <Box position="absolute" bottom={8} right={8}>
            <Chip
              size="small"
              label={chatStatus}
              sx={{
                bgcolor: "#cef8fa",
                borderRadius: 1,
                color: "#0dc3d3",
                fontWeight: "bold",
              }}
            />
          </Box>
        </Stack>
      </CustomToolbar>
    </Stack>
  );
};

export default ChatHeader;
